import { useState, useMemo, useRef, useEffect, CSSProperties } from 'react';
import css from './index.module.scss';
import SupportContent from './suportContent';
import type { FAQ, Download } from './suportContent';
import { useGlobal } from '@/utils/useGlobal';
import MenuA from '@/efelement/EFMenu';

export interface SupportData {
    faqList: FAQ[] | [];
    downloadList: Download[] | [];
    videoList: Download[] | [];
}
export type ProductSupportProps = {
    theme: string;
    supportData: SupportData;
    style?: CSSProperties;
};

type SupportMenuItem = {
    label: string;
    key: string;
    children?: Array<SupportMenuItem>;
};
export type SupportMenu = Array<SupportMenuItem>;

export const SUPPORT_MENU_KEYS = {
    FAQ: 'faq',
    INTRODUCTION: 'introduction',
    TROUBLESHOOTING: 'troubleshooting',
    NOTE: 'note',
    TUTORIAL: 'tutorial',
    DOWNLOADS: 'download',
    CONTACT_US: 'contact_us',
    ONLINE: 'online',
};

export default function ProductSupport({ theme = '', supportData, style = {} }: ProductSupportProps) {
    const { isMobile, useLang } = useGlobal();
    const [currentKey, setCurrentKey] = useState(SUPPORT_MENU_KEYS.INTRODUCTION);
    const { t } = useLang('shared');

    const SUPPORT_MENU: SupportMenu = [
        {
            label: t('support.tab5'),
            key: 'faq',
            children: [
                { label: t('support.tab6'), key: SUPPORT_MENU_KEYS.INTRODUCTION },
                { label: t('support.tab7'), key: SUPPORT_MENU_KEYS.TROUBLESHOOTING },
                { label: t('support.tab8'), key: SUPPORT_MENU_KEYS.NOTE },
            ],
        },
        { label: t('support.tab9'), key: SUPPORT_MENU_KEYS.TUTORIAL },
        { label: t('support.tab4'), key: SUPPORT_MENU_KEYS.DOWNLOADS },
        // { label: t('support.tab11'), key: SUPPORT_MENU_KEYS.ONLINE },
        // { label: t('support.tab10'), key: SUPPORT_MENU_KEYS.CONTACT_US },
    ];

    const allMenus = SUPPORT_MENU.flatMap((item) => [item, ...(item.children || [])]);

    const handleClick = ({ item, key }: any) => {
        setCurrentKey(key);
    };

    const selectedFaqKey = useMemo(() => {
        if ([SUPPORT_MENU_KEYS.INTRODUCTION, SUPPORT_MENU_KEYS.TROUBLESHOOTING, SUPPORT_MENU_KEYS.NOTE, SUPPORT_MENU_KEYS.FAQ].includes(currentKey)) {
            return SUPPORT_MENU_KEYS.FAQ;
        }
        return currentKey;
    }, [currentKey]);

    return (
        <div className={`${css.product_support} ${theme}`} style={{ width: !isMobile ? 1120 : 'auto', marginTop: isMobile ? 20 : 39, ...style }}>
            {isMobile && (
                <div className="tab-nav">
                    <ScrollTabs
                        items={SUPPORT_MENU}
                        defaultKey={SUPPORT_MENU_KEYS.FAQ}
                        onTabClick={(key: string) => {
                            if (key === SUPPORT_MENU_KEYS.FAQ) {
                                return setCurrentKey(SUPPORT_MENU_KEYS.INTRODUCTION);
                            }
                            setCurrentKey(key);
                        }}
                        theme={theme}
                    />
                    {selectedFaqKey === SUPPORT_MENU_KEYS.FAQ && (
                        <div className="content">
                            <div className="faq-sub-tab">
                                {SUPPORT_MENU?.[0]?.children?.map((item) => (
                                    <div key={item.key} className={`item ${currentKey === item.key ? 'active' : ''}`} onClick={() => setCurrentKey(item.key)}>
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {supportData && <SupportContent selectedkey={currentKey} supportData={supportData} theme={theme} />}
                </div>
            )}
            {!isMobile && (
                <>
                    <div className="nav">
                        <div className="menu_wrap" style={{ width: 250 }}>
                            <MenuA data={SUPPORT_MENU} onClick={handleClick} theme={theme === 'black' ? 'dark' : 'light'} />
                        </div>
                    </div>
                    <div className="content">
                        {supportData && <SupportContent selectedkey={currentKey} supportData={supportData} theme={theme} allMenus={allMenus} />}
                    </div>
                </>
            )}
        </div>
    );
}

type ScrollTabsProps = {
    onTabClick?: (key: string) => void;
    items: {
        key: string;
        label: string;
    }[];
    defaultKey: string;
    theme: string;
};

export function ScrollTabs({ onTabClick, items, defaultKey, theme }: ScrollTabsProps) {
    const containerRef = useRef<HTMLUListElement>(null);
    const [selectedKey, setSelectedKey] = useState<string>(defaultKey);
    useEffect(() => {
        const container = containerRef.current;
        const handleClick = (event: Event) => {
            const target = event.target as HTMLElement;
            if (target.tagName === 'LI') {
                const key = target.getAttribute('id') || '';
                onTabClick?.(key);
                setSelectedKey(key);
            }
        };
        container?.addEventListener('click', handleClick);
        return () => {
            container?.removeEventListener('click', handleClick);
        };
    }, []);

    return (
        <ul ref={containerRef} className={`${css.scroll_tabs} scroll-tabs ${theme}`}>
            {items.map(({ key, label }) => (
                <li key={key} id={key} className={`tab_item ${key === selectedKey ? 'tab_item_active' : ''}`}>
                    {label}
                </li>
            ))}
        </ul>
    );
}
